import type {
  OrganizationMembershipStatus,
  UserWithSpecificOrganizationMembership,
} from "@arbeidsark/shared"
import { useCallback } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import {
  Button,
  type DropdownItemProps,
  QuoTable,
  TableRowMenu,
} from "~/components"
import type { Columns } from "~/components/QuoTable/types"
import { useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"

const Layout = styled.div`
  padding: 1rem 1.5rem;
`

const NameCell = styled.span`
  font-weight: 500;
  color: ${(props) => props.theme.colors.gray0};
`

const ActiveCell = styled.span<{ status: OrganizationMembershipStatus }>`
  color: ${(props) =>
    props.status === "ACTIVE"
      ? props.theme.colors.primary40
      : props.status === "INACTIVE"
        ? props.theme.colors.error60
        : props.theme.colors.warning50};
`

const RowMenuWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`

export type UsersListProps = {}

const UsersList: React.FC<UsersListProps> = () => {
  const translations = useTranslate({
    name: "common.name",
    email: "common.email",
    status: "common.status",
    statusActive: "common.active",
    statusInactive: "common.inactive",
    pending: "common.pending",
    addUser: "users.add-user",
    menuItemLabels: {
      deactivate: "common.deactivate",
      activate: "common.activate",
      accept: "users.accept-user-join-request",
      reject: "users.reject-user-join-request",
      resetPassword: "users.reset-password",
      remove: "users.remove-user-from-organization",
    },
    toasts: {
      userActivated: "users.user-activated",
      userDeactivated: "users.user-deactivated",
      passwordResetSuccess: "users.admin-password-reset-success",
      userRemovedSuccess: "users.user-removed-success",
    },
  })

  const getActivityStatus = useCallback(
    (status: OrganizationMembershipStatus) => {
      switch (status) {
        case "ACTIVE":
          return translations.statusActive
        case "INACTIVE":
          return translations.statusInactive
        case "PENDING":
          return translations.pending
      }
    },
    [translations],
  )

  // This will always be defined in this view as it is prerequisite for the view to render
  const organizationMembership = useOrganizationMembership()!

  const { data, isLoading } = trpc.users.getOrganizationUsers.useQuery({
    organizationId: organizationMembership.organization.id,
    filter: {},
  })

  const trpcUtils = trpc.useContext()
  const toast = useToast()
  const { trpcErrorHandler } = useErrorHandling()

  const { mutate: setUserMembershipStatus } =
    trpc.organizations.setUserMembershipStatus.useMutation({
      onSuccess: (data) => {
        trpcUtils.users.getOrganizationUsers.invalidate({
          organizationId: organizationMembership.organization.id,
          filter: {},
        })
        toast(
          "success",
          data.organizationMembership?.status === "ACTIVE"
            ? translations.toasts.userActivated
            : translations.toasts.userDeactivated,
        )
      },
      onError: trpcErrorHandler(toast),
    })

  const { mutate: removeUserFromOrganization } =
    trpc.organizations.removeUserFromOrganization.useMutation({
      onSuccess: () => {
        trpcUtils.users.getOrganizationUsers.invalidate({
          organizationId: organizationMembership.organization.id,
          filter: {},
        })
        toast("success", translations.toasts.userRemovedSuccess)
      },
      onError: trpcErrorHandler(toast),
    })

  const { mutate: resetUserPassword } =
    trpc.organizations.adminResetUserPassword.useMutation({
      onSuccess: () => {
        toast("success", translations.toasts.passwordResetSuccess)
      },
      onError: trpcErrorHandler(toast),
    })

  // const navigate = useNavigate()

  const tableColumns: Columns[] = [
    {
      id: "name",
      label: translations.name,
    },
    {
      id: "email",
      label: translations.email,
    },
    {
      id: "isActive",
      label: translations.status,
    },
    {
      id: "rowMenu",
      label: (
        <Link to="/users/add">
          <Button width="224px">{translations.addUser}</Button>
        </Link>
      ),
      hide:
        organizationMembership.role !== "ADMIN" &&
        organizationMembership.role !== "OWNER",
      width: "204px",
    },
  ]

  // This callback contains some utility items for the row menu we use
  // in the table. We use this to avoid having a lot of complex logic below.
  const getRowMenuItems = useCallback(
    (user: UserWithSpecificOrganizationMembership) => {
      let items: DropdownItemProps[]

      // If the user is pending, we only add the "accept" and "deny" items to it
      if (user.organizationMembership?.status === "PENDING") {
        items = [
          {
            label: translations.menuItemLabels.accept,
            onClick: () => {
              setUserMembershipStatus({
                userId: user.id,
                organizationId: user.organizationMembership?.organizationId!,
                status: "ACTIVE",
              })
            },
          },
          {
            label: translations.menuItemLabels.reject,
            onClick: () => {
              removeUserFromOrganization({
                userId: user.id,
                organizationId: user.organizationMembership?.organizationId!,
              })
            },
          },
        ]
      } else {
        items = [
          {
            label:
              user.organizationMembership?.status === "ACTIVE"
                ? translations.menuItemLabels.deactivate
                : translations.menuItemLabels.activate,
            onClick: () => {
              setUserMembershipStatus({
                userId: user.id,
                organizationId: user.organizationMembership?.organizationId!,
                status:
                  user.organizationMembership?.status === "ACTIVE"
                    ? "INACTIVE"
                    : "ACTIVE",
              })
            },
          },
          {
            label: translations.menuItemLabels.resetPassword,
            onClick: () => {
              resetUserPassword({
                userId: user.id,
                organizationId: user.organizationMembership!.organizationId!,
              })
            },
          },
          {
            label: translations.menuItemLabels.remove,
            onClick: () => {
              removeUserFromOrganization({
                userId: user.id,
                organizationId: user.organizationMembership!.organizationId!,
              })
            },
          },
        ]
        // Add activation or deactivation item depending on the current status
      }

      return items
    },
    [
      setUserMembershipStatus,
      translations,
      resetUserPassword,
      removeUserFromOrganization,
    ],
  )

  const tableData =
    data?.map((user) => ({
      id: user.id.toString(),
      data: {
        name: <NameCell>{user.firstName + " " + user.lastName}</NameCell>,
        email: user.email,
        isActive: (
          <ActiveCell status={user.organizationMembership?.status ?? "PENDING"}>
            {getActivityStatus(
              user.organizationMembership?.status ?? "PENDING",
            )}
          </ActiveCell>
        ),
        rowMenu: (
          <RowMenuWrapper>
            <TableRowMenu items={getRowMenuItems(user)} />
          </RowMenuWrapper>
        ),
      },
    })) ?? []

  return (
    <Layout>
      <QuoTable
        columns={tableColumns}
        data={tableData}
        tableProps={{ fullWidth: true, borderRadius: "8px" }}
        cellProps={{
          lineHeight: "150%",
        }}
        loading={isLoading}
        loadingProps={{
          rows: 4,
          expectedWidths: [100, 100, 100, 100],
        }}
      />
    </Layout>
  )
}

export default UsersList
